// Современные состояния загрузки вместо старого белого экрана со «спиннером-кольцами».
//
// Главное правило: загрузка не закрывает шапку и нижнее меню и не меняет фон —
// вместо содержимого показывается его «скелет» (силуэт карточек), а по готовности
// на то же место встаёт настоящий контент. Ничего не мигает белым и не прыгает.
// Скелеты появляются сразу и без анимации: так скелет из index.html (до загрузки JS),
// скелет первого запуска и скелет списка сменяют друг друга незаметно.
// Индикатор и пелена появляются с задержкой: быстрые ответы их вообще не показывают.
import React from 'react';
import { createPortal } from 'react-dom';
import './loading.css';

// Индикатор: дуга, которая растёт и сжимается, вращаясь (как в Material Design)
export function Spinner({ size, className }) {
    const s = size || 40;
    return (
        <svg className={'ui-spinner' + (className ? ' ' + className : '')} width={s} height={s} viewBox="0 0 50 50" aria-hidden="true">
            <circle className="ui-spinner__track" cx="25" cy="25" r="20" />
            <circle className="ui-spinner__arc" cx="25" cy="25" r="20" />
        </svg>
    );
}

// Скелет карточки задания — повторяет геометрию .task_item_container
function TaskCardSkeleton({ index }) {
    return (
        <div className="ui-skel-card">
            <div className="ui-skel ui-skel--icon" />
            <div className="ui-skel-card__body">
                <div className="ui-skel ui-skel--line" style={{ width: index % 2 ? '58%' : '72%' }} />
                <div className="ui-skel ui-skel--line ui-skel--thin" style={{ width: index % 2 ? '84%' : '64%' }} />
                <div className="ui-skel ui-skel--line ui-skel--thin" style={{ width: index % 2 ? '40%' : '52%' }} />
            </div>
            <div className="ui-skel ui-skel--pill" />
        </div>
    );
}

// inset — список уже внутри контейнера с отступами (например, история выплат)
export function ListSkeleton({ count, inset }) {
    const n = count || 4;
    const items = [];
    for (let i = 0; i < n; i++) items.push(<TaskCardSkeleton key={i} index={i} />);
    return (
        <div className={'ui-skel-list' + (inset ? ' ui-skel-list--inset' : '')} role="status" aria-label="Загрузка">
            {items}
        </div>
    );
}

// Скелет экрана задания — повторяет .task_container: шапка, описание, блок отчёта, кнопка
export function TaskSkeleton() {
    return (
        <div className="ui-skel-task" role="status" aria-label="Загрузка">
            <div className="ui-skel-task__head">
                <div className="ui-skel ui-skel--task-icon" />
                <div className="ui-skel-task__title">
                    <div className="ui-skel ui-skel--line" style={{ width: '70%' }} />
                    <div className="ui-skel ui-skel--pill ui-skel--pill-left" />
                </div>
            </div>
            <div className="ui-skel-task__text">
                <div className="ui-skel ui-skel--line ui-skel--thin" style={{ width: '92%' }} />
                <div className="ui-skel ui-skel--line ui-skel--thin" style={{ width: '78%' }} />
            </div>
            <div className="ui-skel-task__review">
                <div className="ui-skel ui-skel--line ui-skel--on-blue" style={{ width: '64%' }} />
                <div className="ui-skel ui-skel--line ui-skel--thin ui-skel--on-blue" style={{ width: '88%' }} />
                <div className="ui-skel ui-skel--line ui-skel--thin ui-skel--on-blue" style={{ width: '56%' }} />
            </div>
            <div className="ui-skel ui-skel--button" />
        </div>
    );
}

// Скелет списка чатов — те же размеры, что у карточки переписки
// (стили — chat/chats_android.css, подключён в index.js)
export function ChatListSkeleton() {
    const rows = [];
    for (let i = 0; i < 4; i++) {
        rows.push(
            <div className="wch-skel" key={i}>
                <div className="ui-skel ui-skel--icon" />
                <div className="wch-skel__body">
                    <div className="ui-skel ui-skel--line" style={{ width: i % 2 ? '52%' : '68%' }} />
                    <div className="ui-skel ui-skel--line ui-skel--thin" style={{ width: i % 2 ? '80%' : '62%', marginBottom: 0 }} />
                </div>
            </div>
        );
    }
    return <div role="status" aria-label="Загрузка">{rows}</div>;
}

const TASK_ROUTES = ['custom_task', 'task', 'task_ios', 'task_garanted_review'];

// Привязан ли телеграм — запоминаем, чтобы при следующем запуске скелет сразу знал,
// будет ли над списком баннер «Привязать телеграм» (иначе у тех, кто уже привязал,
// баннер мелькал бы в скелете и пропадал, а список подпрыгивал). Читает и index.html.
const TG_BOUND_KEY = 'boot_tg_bound';

export function rememberTelegramBound(bound) {
    try {
        localStorage.setItem(TG_BOUND_KEY, bound ? '1' : '0');
    } catch (e) { /* хранилище недоступно — скелет просто покажет баннер */ }
}

export function isTelegramBoundCached() {
    try {
        return localStorage.getItem(TG_BOUND_KEY) === '1';
    } catch (e) {
        return false;
    }
}

// Скелет по адресу страницы — для первого запуска и пока грузится код экрана.
// banners — баннеры над списком заданий (как в TasksAndroid), чтобы, когда придут
// данные, список не съезжал вниз: telegram — «Привязать телеграм» (показывается,
// пока он не привязан), logout — предупреждение о перелогинах (Android + upmob).
export function RouteSkeleton({ pathname, banners }) {
    const path = String(pathname || (typeof window !== 'undefined' ? window.location.pathname : ''))
        .replace(/^\/+/, '').split('/')[0].split('?')[0];
    if (TASK_ROUTES.indexOf(path) !== -1) return <TaskSkeleton />;
    if (path === 'chats') {
        // открытая переписка рисуется поверх всего экрана — до её загрузки просто фон
        if (typeof window !== 'undefined' && /[?&]dialog=/.test(window.location.search)) return null;
        return (
            <div className="wch-page">
                <div className="wch-head"><h1 className="wch-title">Чаты</h1></div>
                <ChatListSkeleton />
            </div>
        );
    }
    if (path === 'tasks' || path === '' ) {
        const b = banners || {};
        const narrow = typeof window !== 'undefined' && window.innerWidth <= 340;
        return (
            <div>
                {b.telegram && <div className="telegram_bind_banner">Привязать телеграм для важных оповещений</div>}
                {b.logout && <div className="banner_dont_logout">⛔ За будущие перелогины на одном устройстве —{!narrow ? <br /> : " "}будет штраф</div>}
                <div className="ui-skel-tabs">
                    <div className="ui-skel ui-skel--tab" />
                    <div className="ui-skel ui-skel--tab ui-skel--tab-wide" />
                </div>
                <ListSkeleton count={4} />
            </div>
        );
    }
    return <ListSkeleton count={3} />;
}

// Универсальная загрузка (её же отдаёт старый компонент Loader):
//   page    — индикатор по центру области контента, шапка и меню остаются на месте;
//   list    — скелет карточек;  task — скелет экрана задания;
//   overlay — полупрозрачная пелена под шапкой поверх уже показанного контента
//             (там, где во время запроса нельзя нажимать на экран).
export function LoadingView({ variant, count, inset, offsetTop }) {
    if (variant === 'list') return <ListSkeleton count={count} inset={inset} />;
    if (variant === 'task') return <TaskSkeleton />;
    if (variant === 'overlay') {
        // в body: у предков может быть transform (нажатие, анимация экрана),
        // из-за которого position: fixed считался бы от них, а не от экрана
        const overlay = (
            <div className="ui-loading-overlay" role="status" aria-label="Загрузка">
                <Spinner />
            </div>
        );
        return typeof document !== 'undefined' && document.body ? createPortal(overlay, document.body) : overlay;
    }
    const style = offsetTop ? { paddingTop: offsetTop } : undefined;
    return (
        <div className="ui-loading ui-loading-page" style={style} role="status" aria-label="Загрузка">
            <Spinner />
        </div>
    );
}
